"use client";
import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
} from "framer-motion";
import whyBhupendra from "../asstes/images/private-council/why-bhupendra.jpg";

const useParallax = (value, distance) =>
  useTransform(value, [0, 1], [-distance, distance]);

const WhyBhupendra = () => {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const smooth = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 22,
    mass: 0.4,
  });

  const imageY = useParallax(smooth, 90);
  const imageScale = useTransform(smooth, [0, 0.5, 1], [1.15, 1, 1.05]);
  const imageRotate = useTransform(smooth, [0, 1], [-3, 3]);
  const clipPath = useTransform(
    smooth,
    [0, 0.45],
    ["inset(45% 8% 45% 8% round 24px)", "inset(0% 0% 0% 0% round 24px)"],
  );
  const frameOffset = useTransform(smooth, [0, 0.5], [40, 0]);
  const frameOpacity = useTransform(smooth, [0.05, 0.4], [0, 1]);
  const glowOpacity = useTransform(smooth, [0.1, 0.5, 0.9], [0, 0.7, 0.4]);

  const eyebrowX = useTransform(smooth, [0.1, 0.4], [-40, 0]);
  const eyebrowOpacity = useTransform(smooth, [0.1, 0.4], [0, 1]);
  const titleY = useTransform(smooth, [0.15, 0.55], [80, 0]);
  const titleOpacity = useTransform(smooth, [0.15, 0.5], [0, 1]);
  const italicX = useTransform(smooth, [0.2, 0.6], [60, 0]);
  const italicOpacity = useTransform(smooth, [0.2, 0.55], [0, 1]);
  const p1Y = useTransform(smooth, [0.3, 0.65], [40, 0]);
  const p1Opacity = useTransform(smooth, [0.3, 0.6], [0, 1]);
  const p2Y = useTransform(smooth, [0.4, 0.75], [40, 0]);
  const p2Opacity = useTransform(smooth, [0.4, 0.7], [0, 1]);
  const quoteY = useTransform(smooth, [0.5, 0.85], [40, 0]);
  const quoteOpacity = useTransform(smooth, [0.5, 0.8], [0, 1]);
  const quoteBarHeight = useTransform(smooth, [0.55, 0.9], ["0%", "100%"]);

  const bgY = useParallax(smooth, 150);
  const bgRotate = useTransform(smooth, [0, 1], [0, 30]);

  return (
    <section
      ref={sectionRef}
      className="position-relative  overflow-hidden py-5"
    >
      <motion.div
        style={{ y: bgY, rotate: bgRotate, opacity: glowOpacity }}
        className="position-absolute top-25 end-0 translate-middle-y rounded-circle"
        aria-hidden
        style={{ width: 600, height: 600 }}
      >
        <div
          className="w-100 h-100 rounded-circle blur-lg"
          style={{ background: "var(--gradient-radial-gold)" }}
        />
      </motion.div>

      <div
        className="position-absolute top-0 start-0 w-100 h-100 opacity-5"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--gold)) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
        aria-hidden
      />

      <div className="container position-relative">
        <div className="row align-items-center g-5">
          <div className="col-md-6 d-flex justify-content-center justify-content-md-end">
            <div className="position-relative w-100" style={{ maxWidth: 420 }}>
              <motion.div
                style={{
                  x: frameOffset,
                  y: frameOffset,
                  opacity: frameOpacity,
                  borderColor: "#A0722A",
                }}
                className="position-absolute top-0 start-0 w-100 h-100 rounded-4 border"
                aria-hidden
              />

              <motion.div
                style={{ opacity: glowOpacity }}
                className="position-absolute top-50 start-50 translate-middle rounded-4 blur-lg"
                aria-hidden
              >
                <div
                  className="w-100 h-100 rounded-4"
                  style={{
                    background: "var(--gradient-gold)",
                    opacity: 0.25,
                  }}
                />
              </motion.div>

              <motion.div
                style={{ y: imageY, clipPath, WebkitClipPath: clipPath }}
                className="position-relative overflow-hidden rounded-4"
              >
                <motion.img
                  src={whyBhupendra}
                  alt="Dr.Bhupendra Chaudhary"
                  loading="lazy"
                  style={{ scale: imageScale, rotate: imageRotate }}
                  className="img-fluid w-100 object-fit-cover"
                  height={560}
                />
                <div
                  className="position-absolute top-0 start-0 w-100 h-100"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 50%, rgba(0,0,0,.55) 100%)",
                  }}
                />
              </motion.div>
            </div>
          </div>

          <div className="col-md-6">
            <motion.div
              style={{ x: eyebrowX, opacity: eyebrowOpacity }}
              className="d-flex align-items-center gap-3 mb-4"
            >
              <span
                className="d-inline-block bg-warning"
                style={{
                  height: 1,
                  width: 40,
                  color: "#A0722A",
                  border: "1px solid #A0722A",
                }}
              />
              <span
                className="text-uppercase small  letter-spacing-wide"
                style={{ color: "#A0722A" }}
              >
                The Convener
              </span>
            </motion.div>

            <h2 className="display-4 fw-normal lh-sm ">
              <motion.span style={{ y: titleY, opacity: titleOpacity }}>
                <span style={{ color: "#A0722A" }}>Why </span>
              </motion.span>
              <motion.span
                style={{ x: italicX, opacity: italicOpacity }}
                className="fst-italic gold-gradient-text"
              >
                Dr.Bhupendra
              </motion.span>
            </h2>

            <motion.p
              style={{ y: p1Y, opacity: p1Opacity }}
              className="mt-4 text-secondary fs-5"
            >
              Dr.Bhupendra Chaudhary has navigated decisions where consequences
              were immediate and irreversible, often in silence, always by
              discipline.
            </motion.p>

            <motion.p
              style={{ y: p2Y, opacity: p2Opacity }}
              className="text-secondary fs-5"
            >
              He brings pattern recognition, systemic intelligence and
              accumulated insight into the nervous system and the decision
              process.
            </motion.p>

            <motion.div
              style={{
                y: quoteY,
                opacity: quoteOpacity,
              }}
              className="position-relative mt-4 "
            >
              <p
                className="fst-italic text-[#000000] fs-5"
                style={{ color: "#A0722A" }}
              >
                The Private Council is the form through which he convenes,
                clearly and privately, to surface what a decision will require
                the individual to carry — without reassurance or dilation.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyBhupendra;
